.card {
  cursor: pointer;
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: var(--tuna-palette-background-default);
  border-radius: var(--tuna-shape-borderRadius);
}

@media (prefers-reduced-motion: no-preference) {
  .card {
    transition:
      background-color 0.2s,
      opacity 0.2s;
  }
}

.card:hover {
  background-color: var(--tuna-palette-action-hover) !important;
  border-radius: var(--tuna-shape-borderRadius);
}

.hasImage:hover {
  opacity: 0.7;
}

.backgroundImage {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
}

.headerOverlay {
  background-color: rgba(var(--tuna-palette-background-paper-channel), 0.8);
  z-index: 1;
}

.headerContent {
  flex: 1;
  overflow: hidden;
}

/* Title row: optional pin glyph, then the name, which keeps its own ellipsis. */
.titleRow {
  display: flex;
  align-items: center;
  gap: var(--tuna-spacing-0_5);
  min-width: 0;
}

.pinGlyph {
  display: inline-flex;
  flex: 0 0 auto;
  color: var(--tuna-content-secondary);
}

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mediaContent {
  position: relative;
  flex: 1;
  cursor: pointer;
  max-height: 300px;
  padding: 16px;
}

.mediaContentOverlay {
  z-index: 1;
  background-color: rgba(var(--tuna-palette-background-paper-channel), 0.8);
}

/* Drop into a container (GH #3164 §3, kind B): an inset outline, so the card keeps its size. Never green. */
.dropAccept {
  outline: 2px solid var(--tuna-palette-primary-main);
  outline-offset: -2px;
  background-color: var(--tuna-palette-action-selected);
}

.dropRefuse {
  outline: 2px dashed var(--tuna-palette-divider);
  outline-offset: -2px;
  cursor: not-allowed;
}

/* What the drop will do, or why it won't, inside the card */
.dropCaption {
  position: absolute;
  top: var(--tuna-spacing-1);
  left: var(--tuna-spacing-1);
  padding: 2px var(--tuna-spacing-1);
  border-radius: var(--tuna-shape-radius-sm);
  background-color: var(--tuna-palette-background-paper);
  color: var(--tuna-palette-text-primary);
  font-size: var(--tuna-typography-caption-fontSize);
  pointer-events: none;
}

.actions {
  display: flex;
  align-items: center;
  padding: 8px;
}

.headerActions {
  align-items: center;
  padding-left: 8px;
}

/* The card's "more" action stays visible at low opacity so it is reachable
 * by touch and visible to screen readers; it lifts to full opacity when the
 * card is hovered or the button itself receives keyboard focus. The previous
 * opacity:0-on-hover-only pattern hid the action entirely on touch and from
 * keyboard users until first interaction. */
.menuButton {
  cursor: pointer;
  opacity: 0.4;
}

@media (prefers-reduced-motion: no-preference) {
  .menuButton {
    transition: opacity 0.2s;
  }
}

.card:hover .menuButton,
.menuButton:hover,
.menuButton:focus-visible {
  opacity: 1;
}

.lastEditorSlot {
  position: absolute;
  right: 8px;
  bottom: 8px;
  pointer-events: none;
}

@media (prefers-reduced-motion: no-preference) {
  .lastEditorSlot {
    transition: opacity 120ms ease-out;
  }
}

/* Recently-updated grid cards surface a muted "which space" chip as inline
 * metadata text (not a pill — no border, no background). The space name
 * truncates with ellipsis so a long space name never pushes the relative time
 * off the card; the time itself sits outside this class and never truncates. */
.parentSpace {
  color: var(--tuna-palette-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
  vertical-align: bottom;
}

/* Secondary metadata line on grid cards: optional parent-space chip, a middot,
 * then the relative time. The line is a flex row so the space chip can truncate
 * (it owns the ellipsis via .parentSpace) while the time stays whole. */
.secondaryLine {
  display: flex;
  align-items: baseline;
  min-width: 0;
}

/* The relative time must never truncate — it shrinks last and keeps its glyphs. */
.secondaryTime {
  flex: 0 0 auto;
  white-space: nowrap;
}

/* Metadata line under the title (Home Continue: key, tempo, length) */
.meta {
  color: var(--tuna-content-secondary);
}

/* Compact cards: the metadata joins the secondary line (in place of the space) and truncates before the time does */
.inlineMeta {
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--tuna-content-secondary);
}

/* A card with an action beside its open target: header and action share one row */
.withTrailing {
  flex-direction: row;
  align-items: center;
}

/* Stretched link: the open target's hit area covers the whole card; the trailing action sits above it */
.stretchedHeader {
  flex: 1;
  min-width: 0;
}

.stretchedHeader::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.trailing {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--tuna-spacing-1);
  padding-right: var(--tuna-spacing-1);
}
